import { Button, Collapse, Table, notification } from 'antd'
import { ServerState } from '../itemModel'
import { useContext } from 'react'
import { ItemsContext } from '../ItemsRoot'
import { AngularReceipt, ReceiptView, createReceiptViews } from './receiptModel'
import { centsToDollarsDisplay } from '../../displayFormatters'
import { DeleteButton } from '../../common/DeleteButton'
import { UndeleteButton } from '../../common/UndeleteButton'
import { styleDeletedRows } from '../styleDeletedRows'
import { countDistinct, sum } from '../../../helpers/array'
import { PlusOutlined } from '@ant-design/icons'
import { pluralize } from '../../../helpers/strings'
import { DateTime } from 'luxon'

export type Props = {
  receipts: AngularReceipt[]
  onAdd: () => void
  onClick: (receiptId: number) => void
  onDelete: (event: any, receiptId: number) => void
  isDirty: boolean
}

const StyledTable = styleDeletedRows<typeof Table<ReceiptView>>(Table)

export const ReceiptTable = ({ receipts, onAdd, onClick, onDelete, isDirty }: Props) => {
  const { changeSet, jobItemsQuery, dispatch } = useContext(ItemsContext)
  const serverState: ServerState = {
    workareaItems: jobItemsQuery.data?.actualItems ?? [],
    catalogItems: [],
    templateItems: [],
    estimatedQuantities: {},
  }
  const receiptViews = createReceiptViews(receipts, serverState, changeSet)

  const handleDeleteClick = (event: any, receipt: ReceiptView) => {
    event.stopPropagation()
    onDelete(event, receipt.id)
    const type = receipt.isDeleted ? 'RECEIPT_UNDELETED' : 'RECEIPT_DELETED'
    dispatch({ type, serverState, receiptId: receipt.id })
  }

  const distinctVendors = countDistinct(receiptViews.map(x => x.vendor))
  const receiptsTotal = sum(receiptViews, x => x.total)

  const notify = () => notification.warning({ message: 'Please save the job before creating or updating receipts.' })

  const handleAdd = () => {
    if (isDirty) {
      notify()
      return
    }
    onAdd()
  }

  const handleClick = (id: number) => {
    if (isDirty) {
      notify()
      return
    }
    onClick(id)
  }

  return <>
    <Button type="primary" onClick={handleAdd} icon={<PlusOutlined />}>Add Receipt</Button>
    <Collapse ghost items={[
      {
        key: 1,
        label: <>
          <strong>{receipts.length}</strong> {pluralize(receipts.length, 'receipt')} for&nbsp;
          <strong>{distinctVendors}</strong> {pluralize(distinctVendors, 'vendor')},&nbsp;
          <strong>{centsToDollarsDisplay(receiptsTotal)}</strong> in vendor expenses
        </>,
        children: <StyledTable
          dataSource={receiptViews}
          columns={[
            {
              dataIndex: 'date',
              title: 'Date',
              render: (date) => {
                if (!date) {
                  return <></>
                }
                let parsed = DateTime.fromISO(date)
                if (!parsed.isValid) {
                  parsed = DateTime.fromFormat(date, 'MM/dd/yyyy')
                }
                if (!parsed.isValid) {
                  return <></>
                }
                return parsed.toJSDate().toLocaleDateString()
              }
            },
            {
              dataIndex: 'vendor',
              title: 'Vendor'
            },
            {
              dataIndex: 'receiptNumber',
              title: 'Receipt #'
            },
            {
              dataIndex: 'items',
              title: 'Items'
            },
            {
              dataIndex: 'workareas',
              title: 'Workareas',
            },
            {
              dataIndex: 'enteredBy',
              title: 'Entered By',
            },
            {
              dataIndex: 'total',
              title: 'Total',
              align: 'right',
              render: centsToDollarsDisplay
            },
            {
              key: 'actions',
              render: (_, item) => {
                return item.isDeleted
                  ? <UndeleteButton onClick={e => handleDeleteClick(e, item)} />
                  : <DeleteButton onClick={e => handleDeleteClick(e, item)} />
              },
              align: 'right'
            }
          ]}
          onRow={(item) => ({
            onClick: () => handleClick(item.id),
            style: { cursor: 'pointer' }
          })}
          rowClassName={(item) => item.isDeleted ? 'deleted' : ''}
          rowKey="key"
          pagination={false}
        />
      }
    ]} />
  </>
}
export default ReceiptTable
